/**
 * Dashboard charts, drawn as inline SVG.
 *
 * No charting library on purpose. The panel needs four small trend graphs, and a
 * charting dependency would be a few hundred kilobytes shipped to every admin plus
 * a theming fight to make it look like the rest of the pixel UI. A bar chart is
 * a few rects, and doing it here means the colours come from the same CSS
 * variables as everything else.
 *
 * Everything is pure: the series in, an SVG element out. That keeps them testable
 * without a DOM layout engine — the geometry is arithmetic, and the tests assert
 * the arithmetic rather than the rendering.
 */

const NS = 'http://www.w3.org/2000/svg';

const svgEl = <K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] => {
  const n = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
};

/**
 * Bar heights as a fraction of the plot area, 0..1.
 *
 * Scaled to the series MAXIMUM rather than a fixed ceiling, because signups and
 * battles differ by orders of magnitude and a shared scale would flatten one of
 * them into the axis. An all-zero series returns all zeros instead of dividing by
 * zero and painting NaN-height bars (which render as nothing, so the graph would
 * silently look "empty" rather than "quiet").
 */
export function barFractions(values: number[]): number[] {
  const max = Math.max(0, ...values);
  if (max <= 0) return values.map(() => 0);
  return values.map((v) => Math.max(0, v) / max);
}

/** Short axis label for a YYYY-MM-DD day, e.g. "8 Aug". Empty input → empty label. */
export function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCDate()} ${d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })}`;
}

export interface SeriesOpts {
  /** Bar colour; defaults to the gold accent. */
  color?: string;
  /** Formats the hover tooltip's value. Defaults to a plain integer. */
  format?: (v: number) => string;
  height?: number;
}

/**
 * A day-by-day bar chart.
 *
 * Uses a viewBox and 100% width so it scales with its card instead of being
 * measured — the admin panel is responsive and a pixel-sized canvas would need a
 * ResizeObserver to keep up.
 */
export function barChart(days: string[], values: number[], opts: SeriesOpts = {}): SVGSVGElement {
  const H = opts.height ?? 90;
  const W = 300;                       // viewBox units; the element itself is fluid
  const PAD_B = 14;                    // room for the axis labels
  const plot = H - PAD_B;
  const fr = barFractions(values);
  const n = Math.max(1, values.length);
  const slot = W / n;
  const barW = Math.max(1, slot * 0.7);

  const svg = svgEl('svg', {
    viewBox: `0 0 ${W} ${H}`, width: '100%', height: H,
    preserveAspectRatio: 'none', role: 'img',
  });

  // Baseline, so an empty stretch still reads as "zero" rather than as no data.
  svg.append(svgEl('line', {
    x1: 0, y1: plot, x2: W, y2: plot,
    stroke: 'currentColor', 'stroke-opacity': 0.25, 'stroke-width': 1,
  }));

  values.forEach((v, i) => {
    // A non-zero value always gets at least one unit of height: a day with one
    // signup against a max of 400 would otherwise round to an invisible bar and
    // read as nothing happening.
    const h = v > 0 ? Math.max(1.5, fr[i] * (plot - 2)) : 0;
    const rect = svgEl('rect', {
      x: i * slot + (slot - barW) / 2, y: plot - h, width: barW, height: h,
      fill: opts.color ?? 'var(--color-gold, #ffd97a)', rx: 1,
    });
    const fmt = opts.format ?? ((x: number) => String(x));
    // A <title> child is the no-JS tooltip: hovering a bar names its day and value,
    // which is the only way to read an individual day on a 30-bar axis.
    const tip = svgEl('title', {});
    tip.textContent = `${dayLabel(days[i] ?? '')}: ${fmt(v)}`;
    rect.append(tip);
    svg.append(rect);
  });

  // Only first and last labels — a 30-day axis cannot show thirty dates legibly.
  if (days.length) {
    const label = (x: number, anchor: string, text: string) => {
      const t = svgEl('text', {
        x, y: H - 3, 'text-anchor': anchor, 'font-size': 9, fill: 'currentColor', 'fill-opacity': 0.55,
      });
      t.textContent = text;
      return t;
    };
    svg.append(label(0, 'start', dayLabel(days[0])));
    svg.append(label(W, 'end', dayLabel(days[days.length - 1])));
  }
  return svg;
}

/** Total of a series — the headline number above each graph. */
export const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);
